import { useMemo, useState } from 'react';
import { Alert, Button, Stack, TextField } from '@mui/material';
import _ from 'lodash';

import { GameElementPicker } from '../../shared/GameElementPicker/GameElementPicker';
import { AdvancedOptions } from './giftFormParts/AdvancedOptions';
import { PublicationsSection } from './giftFormParts/PublicationsSection';
import { WrappingField } from './giftFormParts/WrappingField';

import {
    GameElementDto,
    isOnMain,
    preferMainById,
    useGameElementsIncludingBranches,
} from '../../hooks/gameElements';
import { GiftSchemas } from '../../hooks/useGiftApi';

import { assert } from '../../utils/assert';

import { Gift, Publication, PublicationWindow } from './giftFormParts/types';

import { PLACEHOLDER_GIFT } from './giftFormParts/giftFormUtils';
import { usePublicationDrafts } from './giftFormParts/usePublicationDrafts';

export interface GiftFormValue {
    id: string;
    name: string;
    contents: string[];
    wrapperGameElementId: string;
    tutorial: boolean;
    defaultClaimed: boolean;
    minimumVersion: string | null;
    jiraIssueKey: string | null;
    /** Only set when creating a gift; existing gifts are scheduled via publications. */
    publication?: PublicationWindow;
}

interface GiftFormProps {
    gameElements: Map<string, GameElementDto>;
    defaultValue?: GiftSchemas['FullGiftDTO'];
    publications?: GiftSchemas['FullGiftAdminItem']['publications'];
    onSave?: (value: GiftFormValue) => Promise<unknown>;
    onCreatePublication?: (window: PublicationWindow) => Promise<Publication>;
    onUpdatePublication?: (
        publicationId: string,
        window: PublicationWindow,
    ) => Promise<void>;
    onDeletePublication?: (publicationId: string) => Promise<void>;
    onCancel?: () => void;
}

export const GiftForm = ({
    gameElements,
    defaultValue,
    publications,
    onSave,
    onCreatePublication,
    onUpdatePublication,
    onDeletePublication,
    onCancel,
}: GiftFormProps) => {
    const [baseline, setBaseline] = useState<Gift>(() =>
        defaultValue ? { ...defaultValue } : PLACEHOLDER_GIFT,
    );
    const [gift, setGift] = useState(baseline);
    const isNewGift = !defaultValue?.id;

    const publicationState = usePublicationDrafts({
        publications,
        isNewGift,
        onCreatePublication,
        onUpdatePublication,
        onDeletePublication,
    });

    const updateGift = (patch: Partial<Gift>) =>
        setGift((current) => ({ ...current, ...patch }));

    const selectedGameElements = useMemo(
        () =>
            gift.contents
                ?.map((gameElementId) => gameElements.get(gameElementId))
                .filter((element) => element !== undefined) ?? [],
        [gift.contents, gameElements],
    );

    const branchesQuery = useGameElementsIncludingBranches();
    // The game only sees main, so these would ship invisible if the branch isn't merged.
    const branchOnlyReferences = useMemo(() => {
        const rows = branchesQuery.data?.gameElements;
        if (!rows) return [];
        const byId = preferMainById(rows);
        const referenced = [
            ...(gift.contents ?? []),
            ...(gift.wrapperGameElementId ? [gift.wrapperGameElementId] : []),
        ];
        const result: Array<{ id: string; element: GameElementDto }> = [];
        for (const id of referenced) {
            const element = byId.get(id);
            if (element && !isOnMain(element)) {
                result.push({ id, element });
            }
        }
        return result;
    }, [branchesQuery.data, gift.contents, gift.wrapperGameElementId]);

    const isUnsaved = useMemo(
        () => !_.isEqual(gift, baseline),
        [gift, baseline],
    );

    const initialPublication = publicationState.drafts[0];
    const missingDates =
        isNewGift &&
        (!initialPublication?.startDate || !initialPublication?.endDate);

    const saveGift = async () => {
        if (!onSave || missingDates) {
            return;
        }

        const {
            name,
            contents = [],
            wrapperGameElementId,
            tutorial = false,
            defaultClaimed = false,
            minimumVersion,
            jiraIssueKey,
        } = gift;

        assert(name, 'name is undefined');
        assert(wrapperGameElementId, 'wrapperGameElementId is undefined');
        assert(contents, 'contents is undefined');

        const id = gift.id ?? crypto.randomUUID();

        await onSave({
            name,
            contents,
            wrapperGameElementId,
            id,
            tutorial,
            defaultClaimed,
            minimumVersion: minimumVersion ?? null,
            jiraIssueKey: jiraIssueKey ?? null,
            ...(isNewGift &&
            initialPublication?.startDate &&
            initialPublication.endDate
                ? {
                      publication: {
                          startDate: new Date(
                              initialPublication.startDate,
                          ).toISOString(),
                          endDate: new Date(
                              initialPublication.endDate,
                          ).toISOString(),
                      },
                  }
                : {}),
        });

        const saved = { ...gift, id };
        setGift(saved);
        setBaseline(saved);
    };

    const cancel = () => {
        onCancel?.();
        setGift(baseline);
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                saveGift();
            }}
        >
            <Stack sx={{ margin: 2 }} spacing={2}>
                <TextField
                    value={gift.name}
                    size="small"
                    onChange={(e) => updateGift({ name: e.target.value })}
                    label="Name"
                    required
                />

                <WrappingField
                    gameElements={gameElements}
                    wrapperGameElementId={gift.wrapperGameElementId}
                    onChange={(wrapperGameElementId) =>
                        updateGift({ wrapperGameElementId })
                    }
                />

                <GameElementPicker
                    label="Game Elements"
                    value={selectedGameElements}
                    size="small"
                    multiple
                    includeBranches
                    onChange={(elements) => {
                        if (elements) {
                            updateGift({
                                contents: elements.map((ge) => ge.id),
                            });
                        }
                    }}
                />

                {branchOnlyReferences.length > 0 && (
                    <Alert severity="warning" variant="outlined">
                        {branchOnlyReferences.length === 1
                            ? 'This gift references a game element that only exists on a Playset feature branch. The game will not see it in-game until the branch is merged.'
                            : `This gift references ${branchOnlyReferences.length} game elements that only exist on Playset feature branches. The game will not see them in-game until each branch is merged.`}
                        <ul style={{ margin: '0.5em 0 0 0' }}>
                            {branchOnlyReferences.map(({ id, element }) => (
                                <li key={id}>
                                    <code>{element.name}</code> — branch{' '}
                                    <code>{element.branch}</code>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}

                <AdvancedOptions
                    gift={gift}
                    idLocked={Boolean(defaultValue?.id)}
                    onChange={updateGift}
                />

                <PublicationsSection
                    state={publicationState}
                    isNewGift={isNewGift}
                    canUpdate={Boolean(onUpdatePublication)}
                    canDelete={Boolean(onDeletePublication)}
                />

                <Stack direction="row" spacing={1}>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={!isUnsaved || missingDates}
                        type="submit"
                    >
                        Save
                    </Button>
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            cancel();
                        }}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </form>
    );
};
